import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOS } from './OSContext';
import apps, { getApp } from './apps';
import projects from '../../data/projects';

const SYSTEM_ACTIONS = [
  { id: 'game-mode', title: 'Game Mode', subtitle: 'Switch theme', icon: 'fa-solid fa-gamepad', theme: 'v2' },
];

const Spotlight = ({ open, onClose }) => {
  const { openApp, setTheme } = useOS();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const normalizedQuery = query.trim().toLowerCase();

  const appResults = useMemo(() => {
    if (!normalizedQuery) return apps;
    return apps.filter((app) => app.title.toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery]);

  const projectResults = useMemo(() => {
    if (!normalizedQuery) return [];
    return projects.filter((project) => project.title.toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery]);

  const systemResults = useMemo(() => {
    if (!normalizedQuery) return SYSTEM_ACTIONS;
    return SYSTEM_ACTIONS.filter((action) => action.title.toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery]);

  const handleAppClick = (app) => {
    openApp(app);
    onClose();
  };

  const handleProjectClick = () => {
    const projectsApp = getApp('projects');
    if (projectsApp) {
      openApp(projectsApp);
    }
    onClose();
  };

  const handleSystemActionClick = (action) => {
    setTheme(action.theme);
    onClose();
  };

  const hasResults = appResults.length > 0 || projectResults.length > 0 || systemResults.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="spotlight-backdrop"
          className="fixed inset-0 z-[999] flex items-start justify-center bg-black/40 backdrop-blur-sm pt-[12vh] px-4 font-os"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            key="spotlight-box"
            className="w-full overflow-hidden rounded-xl border border-white/10 bg-os-surface2/95 shadow-2xl backdrop-blur-xl"
            style={{ width: 'clamp(320px, 90vw, 560px)' }}
            initial={{ opacity: 0, scale: 0.94, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: -8 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
              <i className="fa-solid fa-magnifying-glass text-os-textdim" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Spotlight Search"
                className="w-full bg-transparent text-base text-os-text placeholder:text-os-textdim outline-none"
              />
            </div>

            {normalizedQuery && (
              <div className="max-h-[50vh] overflow-y-auto py-2">
                {!hasResults && (
                  <div className="px-4 py-6 text-center text-sm text-os-textdim">
                    No results for &ldquo;{query}&rdquo;
                  </div>
                )}

                {appResults.length > 0 && (
                  <div className="px-2 pb-1">
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-os-textdim">
                      Applications
                    </div>
                    {appResults.map((app) => (
                      <button
                        key={app.id}
                        type="button"
                        onClick={() => handleAppClick(app)}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-os-text transition-colors hover:bg-os-accent hover:text-white"
                      >
                        <i className={`${app.icon} w-5 text-center text-os-accent group-hover:text-white`} />
                        <span>{app.title}</span>
                      </button>
                    ))}
                  </div>
                )}

                {projectResults.length > 0 && (
                  <div className="px-2 pb-2">
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-os-textdim">
                      Projects
                    </div>
                    {projectResults.map((project) => (
                      <button
                        key={project.title}
                        type="button"
                        onClick={handleProjectClick}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-os-text transition-colors hover:bg-os-accent hover:text-white"
                      >
                        <i className="fa-solid fa-diagram-project w-5 text-center text-os-accent" />
                        <span className="truncate">{project.title}</span>
                      </button>
                    ))}
                  </div>
                )}

                {systemResults.length > 0 && (
                  <div className="px-2 pb-2">
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-os-textdim">
                      System
                    </div>
                    {systemResults.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => handleSystemActionClick(action)}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-os-text transition-colors hover:bg-os-accent hover:text-white"
                      >
                        <i className={`${action.icon} w-5 text-center text-os-accent group-hover:text-white`} />
                        <span>{action.title}</span>
                        <span className="ml-auto text-xs text-os-textdim">{action.subtitle}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Spotlight;
